import { STATUS_BADGE_VARIANT } from '@vueron/kiska/component';
import type { FrameHistoryStatusDisplay, Modeling } from '../type';
import type { TranslationKey } from '../i18n';

export const MY_WORK_ROUTE_PATH = '/my-work';

export enum TASK_WORKSTATE {
  NONE = 'None',
  PENDING = 'Pending',
  READY = 'Ready',
  PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  FAIL = 'Fail',
}

export enum TASK_DISTRIBUTE_METHOD {
  NORMAL = 'Normal',
  BALANCE = 'Balance',
  RANGE = 'Range',
  RATIO = 'Ratio',
}

export const TASK_WORKSTATE_TRANSLATION_MAPPER: {
  [K in TASK_WORKSTATE]: TranslationKey;
} = {
  [TASK_WORKSTATE.NONE]: 'task.noneState.recompound',
  [TASK_WORKSTATE.PENDING]: 'task.pendingState.accomplicity',
  [TASK_WORKSTATE.READY]: 'task.readyState.reindict',
  [TASK_WORKSTATE.PROGRESS]: 'task.progressState.metazoal',
  [TASK_WORKSTATE.COMPLETED]: 'task.completedState.chemicalization',
  [TASK_WORKSTATE.FAIL]: 'task.failState.antipyrine',
} as const;

export enum TASK_FRAME_WORKSTATE {
  WORKING = 'Working',
  PREWORK = 'Prework',
  REVIEW = 'Review',
  REVIEWING = 'Reviewing',
  DONE = 'Done',
  REJECT = 'Reject',
  VIEW_ONLY = 'ViewOnly',
}

export enum TASK_FRAME_WORK_TYPE {
  REVIEW = 'Review',
  REWORK = 'Rework',
  WORK = 'Work',
}

/**
 * Task Detail 벌크 툴바에서 제공하는 프레임 일괄 액션 종류입니다.
 * REDISTRIBUTE 는 작업 단계 재배정으로 별도 모달에서 처리하며 카운트를 표시하지 않습니다.
 */
export enum TASK_FRAME_BULK_ACTION {
  HANDOVER = 'handover',
  REDISTRIBUTE = 'redistribute',
  REJECT = 'reject',
  VERIFY = 'verify',
}

export enum TASK_OPERATION_TYPE {
  AI_ANNOTATION = 'AIAnnotation',
  BOX_IMPORT = 'BoxImport',
  EXPORT_LABEL = 'ExportLabel',
  MODEL_TRAINING = 'ModelTraining',
  MODEL_DEPLOY = 'ModelDeploy',
  REDISTRIBUTION = 'Redistribution',
  TASK_CREATE = 'TaskCreate',
}

export enum TASK_OPERATION_STATE {
  RUNNING = 'Running',
  COMPLETED = 'Completed',
  CANCELED = 'Canceled',
  FAILED = 'Failed',
}

export const TASK_OPERATION_STATE_STATUS_MAPPER = {
  [TASK_OPERATION_STATE.CANCELED]: STATUS_BADGE_VARIANT.NOT_STARTED,
  [TASK_OPERATION_STATE.COMPLETED]: STATUS_BADGE_VARIANT.COMPLETED,
  [TASK_OPERATION_STATE.FAILED]: STATUS_BADGE_VARIANT.FAILED,
  [TASK_OPERATION_STATE.RUNNING]: STATUS_BADGE_VARIANT.IN_PROGRESS,
} as const;

export const TASK_OPERATION_STATUS_BADGE_TEXT_MAPPER = {
  [STATUS_BADGE_VARIANT.NOT_STARTED]: 'Not Started',
  [STATUS_BADGE_VARIANT.COMPLETED]: 'Available',
  [STATUS_BADGE_VARIANT.FAILED]: 'Failed',
  [STATUS_BADGE_VARIANT.IN_PROGRESS]: 'In Progress',
} as const;

export const TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER = {
  working: 'frame.workingState.fireworm',
  prework: 'frame.preworkState.panegyrizer',
  review: 'frame.reviewState.infeasibleness',
  reviewing: 'frame.reviewingState.preteressential',
  done: 'frame.doneState.hypoendocrinism',
  reject: 'frame.rejectState.swindler',
} as const;

export type TASK_FRAME_STATE_TRANSLATION_KEY_MAPPER_KEY =
  keyof typeof TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER;

export const TASK_CREATE_FAIL_REASON_TRANSLATION_KEY_MAPPER = {
  'miri.task.error.oslo': 'task.createFailReason.oslo',
  'miri.task.error.file': 'task.createFailReason.file',
  'miri.task.error.storage': 'task.createFailReason.storage',
  'miri.task.error.empty': 'task.createFailReason.empty',
  'miri.task.error.frame_plan': 'task.createFailReason.framePlan',
  'miri.task.error.db': 'task.createFailReason.db',
  'miri.task.error.redis': 'task.createFailReason.redis',
  'miri.task.error.mq': 'task.createFailReason.mq',
  'miri.task.error.retry': 'task.createFailReason.retry',
  'miri.task.error.unknown': 'task.createFailReason.unknown',
} as const;

export const STATUS_COLOR_MAP: {
  [key in
    | DisplayedStatus
    | Modeling['status']
    | Modeling['deployStatus']
    | 'common.notStarted.disciple'
    | 'common.inProgress.hypopial'
    | 'common.completed.boston'
    | TASK_WORKSTATE
    | TASK_OPERATION_STATE
    | STATUS_BADGE_VARIANT]: string;
} = {
  [TASK_WORKSTATE.NONE]: '#B3B3B3',
  [TASK_WORKSTATE.FAIL]: '#F46057',
  [TASK_WORKSTATE.PENDING]: '#B3B3B3',
  [TASK_WORKSTATE.READY]: '#B3B3B3',
  [TASK_WORKSTATE.PROGRESS]: '#2CCB6F',
  [TASK_WORKSTATE.COMPLETED]: '#2A58F6',

  [STATUS_BADGE_VARIANT.NOT_STARTED]: '#B3B3B3',
  [STATUS_BADGE_VARIANT.IN_PROGRESS]: '#2CCB6F',
  [STATUS_BADGE_VARIANT.COMPLETED]: '#2A58F6',
  [STATUS_BADGE_VARIANT.FAILED]: '#F46057',
  [STATUS_BADGE_VARIANT.REJECTED]: '#DA645D',
  [STATUS_BADGE_VARIANT.AI_ANNO]: '#7DCC49',
  [STATUS_BADGE_VARIANT.IN_REVIEW]: '#AD55FF',
  [STATUS_BADGE_VARIANT.READY]: '#82A8E2',
  [STATUS_BADGE_VARIANT.SUBMITTED]: '#4AACDC',

  [TASK_OPERATION_STATE.CANCELED]: '#82A8E2',
  // [TASK_OPERATION_STATE.COMPLETED]: '#4AACDC',
  [TASK_OPERATION_STATE.FAILED]: '#F46057',
  [TASK_OPERATION_STATE.RUNNING]: '#2CCB6F',

  'Not started': '#B3B3B3',
  'View Only': '#B3B3B3',
  'In progress': '#2CCB6F',
  Training: '#2CCB6F',
  'In review': '#AD55FF',
  Available: '#2A58F6',
  Rejected: '#DA645D',
  // Failed: '#F46057',
  Failure: '#F46057',
  'AI-anno': '#7DCC49',
  Submitted: '#4AACDC',
  Deploying: '#2CCB6F',
  Deployed: '#2A58F6',
  DeployFailure: '#F46057',
  DeployPending: '#B3B3B3',

  'common.notStarted.disciple': '#B3B3B3',
  'common.inProgress.hypopial': '#2CCB6F',
  'common.completed.boston': '#2A58F6',
} as const;

export const FRAME_HISTORY_STATUS_DISPLAY_MAP = {
  'AI-anno': {
    translationKey: 'frame.status.aiAnno',
    variant: STATUS_BADGE_VARIANT.AI_ANNO,
  },
  Completed: {
    translationKey: 'frame.doneState.hypoendocrinism',
    variant: STATUS_BADGE_VARIANT.COMPLETED,
  },
  'In progress': {
    translationKey: 'frame.workingState.fireworm',
    variant: STATUS_BADGE_VARIANT.IN_PROGRESS,
  },
  'In review': {
    translationKey: 'frame.reviewingState.preteressential',
    variant: STATUS_BADGE_VARIANT.IN_REVIEW,
  },
  'Not started': {
    translationKey: 'frame.preworkState.panegyrizer',
    variant: STATUS_BADGE_VARIANT.NOT_STARTED,
  },
  Rejected: {
    translationKey: 'frame.rejectState.swindler',
    variant: STATUS_BADGE_VARIANT.REJECTED,
  },
  Submitted: {
    translationKey: 'frame.reviewState.infeasibleness',
    variant: STATUS_BADGE_VARIANT.SUBMITTED,
  },
  'View Only': {
    translationKey: 'frame.status.viewOnly',
    variant: STATUS_BADGE_VARIANT.NOT_STARTED,
  },
} as const satisfies Partial<
  Record<DisplayedStatus, FrameHistoryStatusDisplay>
>;
